import { CoachPassSettings, noCoachPassLimit } from "../Api/coachPasses";

const MIN_VALUE = 1;
// Keep in sync with CoachPassSettings::MAX_PASSES_PER_BLOCK / MAX_COMPETITORS_PER_BLOCK.
const MAX_PASSES_PER_BLOCK = 20;
const MAX_COMPETITORS_PER_BLOCK = 500;

export type ParsedLimit = { valid: boolean; passesPerBlock: number | null; competitorsPerBlock: number | null };

function parseValue(text: string, max: number): number | null {
    if (!/^\d+$/.test(text)) {
        return null;
    }
    const value = Number(text);

    return value >= MIN_VALUE && value <= max ? value : null;
}

/**
 * Both fields empty means "no limit". Otherwise both must be whole numbers: passes 1..20, competitors 1..500.
 */
export function parseLimit(passesText: string, blockText: string): ParsedLimit {
    const passes = passesText.trim();
    const block = blockText.trim();
    if (passes === "" && block === "") {
        return { valid: true, passesPerBlock: null, competitorsPerBlock: null };
    }
    const passesPerBlock = parseValue(passes, MAX_PASSES_PER_BLOCK);
    const competitorsPerBlock = parseValue(block, MAX_COMPETITORS_PER_BLOCK);

    return {
        valid: passesPerBlock !== null && competitorsPerBlock !== null,
        passesPerBlock,
        competitorsPerBlock,
    };
}

function storageKey(eventId: number): string {
    return `coachPassSettings:${eventId}`;
}

/**
 * Settings are remembered per event in this browser only. Storage can be missing or blocked, then there is no limit.
 */
export function loadCoachPassSettings(eventId: number): CoachPassSettings {
    try {
        const raw = window.localStorage.getItem(storageKey(eventId));
        if (raw === null) {
            return { ...noCoachPassLimit };
        }
        const stored = JSON.parse(raw) as Partial<CoachPassSettings>;
        const limit = parseLimit(String(stored.passesPerBlock ?? ""), String(stored.competitorsPerBlock ?? ""));

        return {
            passesPerBlock: limit.valid ? limit.passesPerBlock : null,
            competitorsPerBlock: limit.valid ? limit.competitorsPerBlock : null,
            fillWithBlank: stored.fillWithBlank === true,
            blankNames: stored.blankNames === true,
        };
    } catch {
        return { ...noCoachPassLimit };
    }
}

export function saveCoachPassSettings(eventId: number, settings: CoachPassSettings): void {
    try {
        window.localStorage.setItem(storageKey(eventId), JSON.stringify(settings));
    } catch {
        // Storage unavailable (private window, blocked site data): settings just won't be remembered.
    }
}
